import { useEffect, useMemo, useRef, useState } from "react";
import { deserializeEditGraph, resolveEdit, serializeEditGraph } from "@desk/core";
import { createGraphForPack, PACK_REGISTRY, SHIFT_PACK_ORDER } from "../content/registry.js";
import { isCastingUnlocked, isCaptionDeepUnlocked, isSoundDeepUnlocked } from "../game/unlocks.js";
import type { GameController } from "../shell/hooks/useGameController.js";
import { storyMotifFor } from "../shell/lib/story-motifs.js";
import { radarPoints, radarSmoothPath } from "../shell/lib/radar-geometry.js";
import {
  EMOTION_AXES,
  editEmotionVector,
  toAxisValues,
  type CrowdEmotionProfile,
  type EmotionVector,
} from "../game/emotion-model.js";
import { landingConfigForPack } from "../content/landing/index.js";
import { crowdChromeForPack } from "../content/landing/crowds-presentation.js";
import { crowdDifficulty, type CrowdDifficultyRead } from "../content/landing/difficulty.js";
import { bestResultForCrowd } from "../game/progress.js";
import { EmotionSpider } from "../shell/ui/StatsPanel.js";

/** Source poster/clip for a pack, derived from its id (all packs ship both). */
function packMediaSrc(packId: string): { poster: string; video: string } {
  const slug = packId.replace(/^scenario-/, "");
  return {
    poster: `/assets/visual/${slug}-master-poster.jpg`,
    video: `/assets/visual/${slug}-master.mp4`,
  };
}

interface Crowd {
  key: string;
  icon: string;
  /** Optional crisp free-licensed Twemoji SVG used in the crowd avatar badge. */
  avatarSrc?: string;
  avatarAlt: string;
  name: string;
  tone: string;
  /** Per-crowd accent used for the card top bar + glow. */
  accent: string;
  wants: string;
  /** True for the pack's served crowd (the default objective). */
  assigned: boolean;
  /** This crowd has already been landed for this story (show a ★). */
  landed: boolean;
  /** Landed clean (no receipts) — the skilled-manipulator win. */
  clean: boolean;
  /**
   * How hard this crowd is to land, MEASURED by the level sim rather than
   * authored. Null when the scenario has no sim numbers yet.
   */
  difficulty: CrowdDifficultyRead | null;
}

/**
 * Small ~64px pentagon thumbnail previewing a crowd's appetite: green wants
 * zone + red backlash zone. Reuses the same radar geometry as the full matrix;
 * presentation only, never feeds scoring.
 */
function CrowdMiniSpider({ profile }: { profile: CrowdEmotionProfile | undefined }) {
  if (!profile) return null;
  const zonePath = (v: EmotionVector) =>
    radarSmoothPath(toAxisValues(v).map((n) => Math.max(n, 0.12)), 24, 32, 32);
  const rings = [1, 0.6].map((ring) =>
    radarPoints(EMOTION_AXES.map(() => ring), 24, 32, 32),
  );
  return (
    <svg viewBox="0 0 64 64" className="brief-mob-spider" aria-hidden>
      {rings.map((pts, i) => (
        <polygon key={i} points={pts} fill="none" stroke="rgba(255,255,255,.09)" strokeWidth="1" />
      ))}
      <path
        d={zonePath(profile.red)}
        fill="rgba(255,61,127,.22)"
        stroke="#ff3d7f"
        strokeWidth="1.2"
        strokeDasharray="2.5 1.8"
      />
      <path
        d={zonePath(profile.green)}
        fill="rgba(51,230,190,.24)"
        stroke="#33e6be"
        strokeWidth="1.3"
      />
    </svg>
  );
}

export function BriefScreen({ game }: { game: GameController }) {
  const { state, dispatch } = game;
  const packId = SHIFT_PACK_ORDER[state.storyIndex] ?? SHIFT_PACK_ORDER[0]!;
  const entry = PACK_REGISTRY[packId];
  const pack = entry.pack;
  const landingConfig = useMemo(() => landingConfigForPack(packId), [packId]);
  // Data-driven crowds: the pack's chrome (icon/avatar/accent/wants) merged with
  // the landing config's labels + served flag, in Brief display order. Presentation
  // only; never feeds scoring.
  const crowds = useMemo<Crowd[]>(
    () =>
      crowdChromeForPack(packId).map((c) => {
        const profile = landingConfig.crowds[c.id];
        const best = bestResultForCrowd(state, packId, c.id);
        return {
          key: c.id,
          icon: c.icon,
          avatarSrc: c.avatarSrc,
          avatarAlt: c.avatarAlt,
          name: profile?.label ?? c.id,
          tone: c.accent,
          accent: c.accent,
          wants: c.wants,
          assigned: c.id === landingConfig.servedCrowdId,
          landed: Boolean(best?.landed),
          clean: Boolean(best?.landed && best?.clean),
          difficulty: crowdDifficulty(packId, c.id),
        };
      }),
    [packId, landingConfig, state],
  );

  // The chosen objective (Decision D5 — default-select the served crowd; the
  // player just confirms it by opening the editor). Derived from state so the
  // selection persists into the editor / publish.
  const selectedCrowdId = state.currentEdit?.chosenCrowdId ?? landingConfig.servedCrowdId;
  const media = useMemo(() => packMediaSrc(packId), [packId]);
  const sourceGraph = useMemo(() => createGraphForPack(packId, 42), [packId]);
  const sourceGraphJson = useMemo(() => serializeEditGraph(sourceGraph), [sourceGraph]);

  // Prefer the player's LIVE reel (current edit, else newest draft for this pack)
  // so the Brief spider plots what they've actually cut. Falls back to the raw
  // source graph (typically near-zero) before any editing has happened.
  const liveGraphJson = useMemo<string | null>(() => {
    if (state.currentEdit && state.currentEdit.packId === packId) {
      return state.currentEdit.graphJson;
    }
    const draft = state.drafts
      .filter((d) => d.packId === packId)
      .sort((a, b) => b.seq - a.seq)[0];
    return draft?.graphJson ?? null;
  }, [state.currentEdit, state.drafts, packId]);
  const hasLiveReel = liveGraphJson !== null;

  const editVector = useMemo<EmotionVector>(() => {
    const graph = liveGraphJson
      ? deserializeEditGraph(liveGraphJson)
      : sourceGraph;
    return editEmotionVector(resolveEdit(graph, pack), landingConfig);
  }, [liveGraphJson, sourceGraph, pack, landingConfig]);
  const trend = pack.trends[0];
  const motif = storyMotifFor(packId);
  const toolHint = (() => {
    if (isSoundDeepUnlocked(state.unlocks) || isCaptionDeepUnlocked(state.unlocks)) {
      return "Deeper caption and sting timing are unlocked.";
    }
    if (isCastingUnlocked(state.unlocks)) {
      return "Casting is unlocked. Try a labeled fictional variant.";
    }
    return "Start with Cut and Frame. Caption and sound stay available.";
  })();

  const storyNo = state.storyIndex + 1;
  const storyTotal = SHIFT_PACK_ORDER.length;
  const durationSec = Math.round(
    pack.beats.reduce((n, b) => Math.max(n, b.endTick), 0) / pack.tickRate,
  );

  const [footageOpen, setFootageOpen] = useState(false);
  const [activeCrowd, setActiveCrowd] = useState<Crowd | null>(null);
  // Emotion-matrix data has bespoke Vet-Day profiles; other packs fall back to
  // the same shape so the interaction never breaks.
  const emotionProfiles = landingConfig.crowds;

  // Screenshot-visible near-zero vs. actual — surfaced only inside the matrix.
  const vectorHasSignal = toAxisValues(editVector).some((v) => v > 0.02);

  return (
    <div
      className="desk-app desk-chrome brief-screen brief-mock"
      data-testid="screen-brief"
      style={{ ["--story-accent" as string]: motif.accent }}
    >
      <div className="brief-inner">
        <div className="brief-topline">
          <button
            type="button"
            className="brief-back-btn"
            data-testid="flow-back"
            aria-label="Back to Desk"
            onClick={() => dispatch({ type: "OPEN_ROADMAP" })}
          >
            ‹
          </button>
          <span className="brief-incoming-tag">
            INCOMING · STORY {storyNo} / {storyTotal}
          </span>
          <span className="brief-topline-spacer" />
        </div>

        <h2 className="brief-title">{entry.title}</h2>
        <p className="brief-sub">
          A studio drop just landed in your bag. Cut it however you like.
        </p>

        <div className="brief-source-stage">
          <button
            type="button"
            className="brief-source brief-source-video"
            data-testid="brief-incoming"
            aria-label="Expand source footage"
            onClick={() => setFootageOpen(true)}
          >
            {/* Static poster on the Brief; the actual clip plays only when enlarged. */}
            <img
              className="brief-source-media"
              data-testid="brief-poster"
              src={media.poster}
              alt="Source footage poster"
              draggable={false}
            />
            <span className="brief-source-expand" aria-hidden>
              ⤢
            </span>
          </button>
          <div className="brief-source-info">
            <span className="brief-source-tag">◉ SOURCE FOOTAGE</span>
            <p className="brief-source-desc">{pack.canonicalRecord.summary}</p>
            <span className="brief-source-metadata">RAW · 0:11 · 9:16 · muted · 4 beats</span>
          </div>
        </div>

        <div className="brief-mobs-head">SAME {durationSec} SECONDS → THREE CROWDS</div>
        <div className="brief-mobs" role="radiogroup" aria-label="Choose your audience">
          {crowds.map((m) => {
            const selected = m.key === selectedCrowdId;
            return (
            <div
              key={m.key}
              role="radio"
              tabIndex={0}
              aria-checked={selected}
              className={`brief-mob${selected ? " brief-mob-selected" : ""}${m.landed ? " brief-mob-landed" : ""}`}
              data-testid={`brief-crowd-${m.key}`}
              data-selected={selected ? "1" : "0"}
              data-landed={m.landed ? "1" : "0"}
              aria-label={`Edit for ${m.name}${
                m.difficulty ? `, ${m.difficulty.label}: ${m.difficulty.detail}` : ""
              }${m.landed ? ", done" : ""}`}
              style={{ ["--crowd-accent" as string]: m.accent }}
              onClick={() => dispatch({ type: "SELECT_AUDIENCE", crowdId: m.key })}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  dispatch({ type: "SELECT_AUDIENCE", crowdId: m.key });
                }
              }}
            >
              <div className="brief-mob-accent" aria-hidden />
              {m.landed ? (
                <span
                  className="brief-mob-done"
                  data-testid={`brief-crowd-done-${m.key}`}
                  aria-hidden
                  title="Done. Re-edit any time"
                >
                  {"✓"}
                </span>
              ) : null}
              <div className="brief-mob-badge" aria-hidden={!selected}>
                {selected ? "◆ EDITING FOR" : " "}
              </div>
              <div className="brief-mob-avatar" aria-hidden>
                {m.avatarSrc ? (
                  <img
                    className="brief-mob-avatar-img"
                    src={m.avatarSrc}
                    alt={m.avatarAlt}
                    width={22}
                    height={22}
                    draggable={false}
                  />
                ) : (
                  <span className="brief-mob-avatar-glyph" role="img" aria-label={m.avatarAlt}>
                    {m.icon}
                  </span>
                )}
              </div>
              <div className="brief-mob-name" style={{ color: m.tone }}>
                {m.name}
              </div>
              {m.difficulty ? (
                <span
                  className="brief-mob-difficulty"
                  data-testid={`brief-crowd-difficulty-${m.key}`}
                  data-tier={m.difficulty.tier}
                  title={`${m.difficulty.label}: ${m.difficulty.detail}.`}
                >
                  <span className="brief-mob-difficulty-dot" aria-hidden />
                  {m.difficulty.label}
                </span>
              ) : null}
              <div className="brief-mob-preview">
                <CrowdMiniSpider profile={emotionProfiles[m.key]} />
              </div>
              <div className="brief-mob-frame">{m.wants}</div>
              <button
                type="button"
                className="brief-mob-detail"
                data-testid={`brief-crowd-detail-${m.key}`}
                aria-label={`${m.name} emotion detail`}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveCrowd(m);
                }}
              >
                view detail
              </button>
            </div>
            );
          })}
        </div>

        <div className="brief-hidden-extras" aria-hidden>
          <div className="brief-objective" data-testid="brief-objective">
            <p className="mission-line">Audience wants a hook: keep the reel short and clear.</p>
            {trend ? (
              <p className="trend-pill" data-testid="brief-trend">
                <span className="score-label">Trend</span> {trend.label ?? trend.id}
              </p>
            ) : null}
          </div>
          <details className="flow-drawer source-notes" data-testid="brief-source-notes">
            <summary>Source notes</summary>
            <div className="source-notes-body">
              <p className="hint-line" data-testid="brief-tool-hint">
                {toolHint}
              </p>
              <ul className="brief-beats">
                {pack.beats.map((b) => (
                  <li key={b.id}>
                    <strong>{b.label}</strong>
                    {b.contextCritical ? <span className="tag">key</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          </details>
        </div>

        <div className="brief-footer">
          <p className="brief-objective-cue" data-testid="brief-objective-cue">
            Editing for{" "}
            <strong style={{ color: crowds.find((c) => c.key === selectedCrowdId)?.accent }}>
              {crowds.find((c) => c.key === selectedCrowdId)?.name ?? "your crowd"}
            </strong>
            . Land your reel in their green.
          </p>
          <button
            type="button"
            className="primary brief-open-editor"
            data-testid="enter-editor"
            onClick={() => dispatch({ type: "ENTER_EDITOR", crowdId: selectedCrowdId })}
          >
            Open Editor
          </button>
        </div>
      </div>

      {footageOpen ? (
        <FootageModal
          onClose={() => setFootageOpen(false)}
          durationSec={durationSec}
          videoSrc={media.video}
          posterSrc={media.poster}
        />
      ) : null}

      {activeCrowd ? (
        <EmotionMatrixModal
          crowd={activeCrowd}
          profile={emotionProfiles[activeCrowd.key]}
          editVector={editVector}
          vectorHasSignal={vectorHasSignal}
          hasLiveReel={hasLiveReel}
          storyNo={storyNo}
          onClose={() => setActiveCrowd(null)}
        />
      ) : null}
    </div>
  );
}

/** Fullscreen source-footage preview — Esc / tap-out / close button dismisses. */
function FootageModal({
  onClose,
  durationSec,
  videoSrc,
  posterSrc,
}: {
  onClose: () => void;
  durationSec: number;
  videoSrc: string;
  posterSrc: string;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="brief-footage-overlay"
      data-testid="brief-footage-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Source footage"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="brief-footage-sheet">
        <button
          type="button"
          ref={closeRef}
          className="brief-footage-close"
          data-testid="brief-footage-close"
          aria-label="Close"
          onClick={onClose}
        >
          ✕
        </button>
        <span className="brief-footage-badge">RAW · 0:{String(durationSec).padStart(2, "0")}</span>
        <video
          className="brief-footage-media"
          src={videoSrc}
          poster={posterSrc}
          muted
          loop
          autoPlay
          playsInline
          controls
        />
      </div>
    </div>
  );
}

/** Per-crowd emotion spider — green (wants) + red (backlash) zones. */
function EmotionMatrixModal({
  crowd,
  profile,
  editVector,
  vectorHasSignal,
  hasLiveReel,
  storyNo,
  onClose,
}: {
  crowd: Crowd;
  profile: CrowdEmotionProfile | undefined;
  editVector: EmotionVector;
  vectorHasSignal: boolean;
  hasLiveReel: boolean;
  /** 1-based story number, for the modal's LVL label. */
  storyNo: number;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="emotion-overlay"
      data-testid="emotion-matrix"
      role="dialog"
      aria-modal="true"
      aria-labelledby="emotion-matrix-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="emotion-sheet">
        <header className="emotion-head">
          <span className="emotion-head-icon" aria-hidden>
            {crowd.icon}
          </span>
          <div className="emotion-head-text">
            <h2 id="emotion-matrix-title" style={{ color: crowd.tone }}>
              {crowd.name}
            </h2>
            <span className="emotion-head-sub">
              {`EMOTION MATRIX · LVL ${storyNo}`}
            </span>
          </div>
          <button
            type="button"
            ref={closeRef}
            className="emotion-close"
            data-testid="close-emotion-matrix"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>

        <div className="emotion-radar-wrap">
          {/* Reuse the editor's shared EmotionSpider for the crowd detail so the
              Brief and "How it's landing" plot the same shape. */}
          {profile ? (
            <EmotionSpider profile={profile} vector={editVector} showVector={vectorHasSignal} />
          ) : null}
        </div>

        <div className="emotion-legend" data-testid="emotion-legend">
          <span className="emotion-legend-item">
            <span className="emotion-dot emotion-dot-green" /> wants
          </span>
          <span className="emotion-legend-item">
            <span className="emotion-dot emotion-dot-red" /> backlash
          </span>
          {vectorHasSignal ? (
            <span className="emotion-legend-item">
              <span className="emotion-dot emotion-dot-edit" /> your reel
            </span>
          ) : null}
        </div>

        <p className="emotion-objective" data-testid="emotion-objective">
          Land your reel in the green, and keep the other crowds out of the red.
        </p>
        {!hasLiveReel && !vectorHasSignal ? (
          <p className="emotion-hint">Your reel is still blank. Start cutting to plot it here.</p>
        ) : !vectorHasSignal ? (
          <p className="emotion-hint">Your reel is neutral so far. Lean the edit to move it here.</p>
        ) : null}
      </div>
    </div>
  );
}
